import {
  useState,
} from 'react'
import {
  useQuery,
} from '@tanstack/react-query'
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
} from 'lucide-react'
import {
  organizationService,
} from '../services/api'
import type {
  PendingTeamSkillChangeResponse,
  TeamSkillResponse,
} from '../client'

export const TEAM_SKILLS_QUERY_KEY = ['organization-team-skills']

const ADMINS_ONLY_INTRO = 'Only admins’ assistants change team skills; other assistants propose changes for an admin to review.'
const EVERYONE_INTRO = 'Every member’s assistant can change team skills.'
const HOW_TO_CHANGE = 'To change one, ask your assistant.'
const EMPTY_MESSAGE = 'No team skills yet. Ask your assistant to write your team’s first one.'

const ACTION_LABELS: Record<PendingTeamSkillChangeResponse['action'], string> = {
  create: 'New skill',
  update: 'Edit',
  remove: 'Removal',
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function SkillRow({ skill }: { skill: TeamSkillResponse }): JSX.Element {
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <li className="py-3">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="w-full flex items-start gap-2 text-left"
      >
        <Chevron className="w-4 h-4 mt-1 text-gray-400 shrink-0" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-medium text-gray-900">{skill.name}</span>
          <span className="block text-sm text-gray-600">{skill.description}</span>
          <span className="block mt-0.5 text-xs text-gray-500">
            {`Version ${skill.version} · changed ${skill.updated_by ? `by ${skill.updated_by} ` : ''}on ${formatDate(skill.updated_at)}`}
          </span>
        </span>
      </button>
      {open ? (
        <div className="mt-3 ml-6 space-y-3">
          <pre className="whitespace-pre-wrap break-words rounded-xl bg-gray-50 border border-gray-200 p-3 text-xs text-gray-800 font-mono">
            {skill.body}
          </pre>
          {skill.history.length > 0 ? (
            <div>
              <h4 className="text-xs font-semibold text-gray-700">Recent changes</h4>
              <ul className="mt-1 space-y-1">
                {skill.history.map((entry, index) => (
                  <li key={`${entry.version ?? 'none'}-${index}`} className="text-xs text-gray-500">
                    {`${entry.version === null ? '' : `v${entry.version} · `}${entry.note}${entry.by ? ` · ${entry.by}` : ''} · ${formatDate(entry.at)}`}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

function PendingList({ pending }: { pending: PendingTeamSkillChangeResponse[] }): JSX.Element {
  return (
    <div className="mt-6">
      <h3 className="text-sm font-semibold text-gray-900">Pending changes</h3>
      <ul className="mt-2 divide-y divide-gray-100">
        {pending.map((change) => (
          <li key={change.id} className="py-2">
            <p className="text-sm text-gray-900">
              <span className="font-medium">{change.skill_name}</span>
              <span className="text-gray-500">{` · ${ACTION_LABELS[change.action]}`}</span>
            </p>
            <p className="text-sm text-gray-600">{change.note}</p>
            <p className="text-xs text-gray-500">
              {`${change.author ? `Proposed by ${change.author} ` : 'Proposed '}on ${formatDate(change.created_at)}`}
              {change.base_behind ? ' · The skill has changed since' : ''}
            </p>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function TeamSkillsSection(): JSX.Element | null {
  const skillsQuery = useQuery({
    queryKey: TEAM_SKILLS_QUERY_KEY,
    queryFn: () => organizationService.getTeamSkills(),
  })
  const page = skillsQuery.data
  if (!page?.enabled) return null

  const skills = page.skills ?? []
  const pending = page.pending ?? []
  const intro = page.writers === 'members' ? EVERYONE_INTRO : ADMINS_ONLY_INTRO

  return (
    <section
      aria-label="Team skills"
      className="bg-white rounded-2xl border border-gray-200 shadow-soft p-6"
    >
      <div className="flex items-center gap-2">
        <span className="text-primary-500"><BookOpen className="w-5 h-5" /></span>
        <h2 className="text-lg font-semibold text-gray-900">Team skills</h2>
      </div>
      <p className="mt-1 text-sm text-gray-500">{`${intro} ${HOW_TO_CHANGE}`}</p>
      {skills.length === 0 ? (
        <p className="mt-4 text-sm text-gray-600">{EMPTY_MESSAGE}</p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100">
          {skills.map((skill) => <SkillRow key={skill.name} skill={skill} />)}
        </ul>
      )}
      {pending.length > 0 ? <PendingList pending={pending} /> : null}
    </section>
  )
}
